import React from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';

import type { PortalUserAuditLogEntry } from '../../../store/modules/merchant/types';
import { getPortalUserAuditTargetName } from '../portalUserAuditNames';

/** Emails are long and table cells truncate, so the full value is shown on hover. */
const EmailText = ({
  email,
  /** Secondary emails sit underneath a name and are de-emphasized. */
  isSecondary = false,
}: {
  email: string;
  isSecondary?: boolean;
}) => (
  <Tooltip title={email} arrow enterDelay={500}>
    <Typography
      noWrap
      sx={{
        fontSize: isSecondary ? 12 : 14,
        color: isSecondary ? 'grey.600' : 'text.primary',
      }}
    >
      {email}
    </Typography>
  </Tooltip>
);

/**
 * Who performed the action. Rendered as a single line of plain text so it reads
 * differently from the two-line target user cell next to it.
 */
export const PortalUserAuditActorCell = ({ auditLog }: { auditLog: PortalUserAuditLogEntry }) => {
  if (!auditLog.actorEmail) {
    return <Typography sx={{ fontSize: 14, color: 'grey.500' }}>—</Typography>;
  }

  return <EmailText email={auditLog.actorEmail} />;
};

/** The user the action was performed on: name on top, email beneath it. */
export const PortalUserAuditTargetUserCell = ({
  auditLog,
}: {
  auditLog: PortalUserAuditLogEntry;
}) => {
  const name = getPortalUserAuditTargetName(auditLog);
  const email = auditLog.targetUserEmail || '—';

  // Without a recorded name the email is the only identifier, so it takes the primary line.
  if (!name) {
    return <EmailText email={email} />;
  }

  return (
    <Stack sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
        {name}
      </Typography>
      <EmailText email={email} isSecondary />
    </Stack>
  );
};
